import { useCallback } from 'react';
import useValueRef from '@alltrails/core/hooks/useValueRef';
import useMap from '../../../hooks/useMap';
import { useAppDispatch, useAppSelector } from '../../../redux';
import { linkActiveSegmentToNextSegment, linkActiveSegmentToPreviousSegment } from '../../../redux/customRoute';
import { Coordinates, LatLng } from '../../../types/Geo';
import { getAdjacentEndpointToLink } from '../../../utils/customRoutes/segmentLinking';
import useCanLinkSegments from '../useCanLinkSegments';
import type { DrawControlsOptions } from './DrawControlsProvider';

export type AdjacentEndpointLinkingOptions = Pick<DrawControlsOptions, 'linkNearbySegmentEndpoints'>;

export type SegmentEnd = 'start' | 'end';

export const LINK_DISTANCE_PX = 20;

/**
 * Snaps a segment's start or end onto the neighboring segment's endpoint when placed within LINK_DISTANCE_PX of it, then links the two.
 * Both callbacks are stable, so they can be used by map event handlers.
 * @returns getAdjacentEndpoint, the neighbor's endpoint when the coordinates are close enough to link, and link, which links the active segment once its end is there
 */
const useAdjacentEndpointLinking = ({ linkNearbySegmentEndpoints = false }: AdjacentEndpointLinkingOptions = {}) => {
  const dispatch = useAppDispatch();
  const canLinkSegments = useCanLinkSegments();
  const map = useMap();
  const { activeSegmentIndex, isInCutMode, segments } = useAppSelector(state => ({
    activeSegmentIndex: state.customRoute.activeSegmentIndex,
    isInCutMode: state.customRoute.cutData !== undefined,
    segments: state.customRoute.route.segments
  }));
  const stateRef = useValueRef({ activeSegmentIndex, isEnabled: linkNearbySegmentEndpoints && canLinkSegments && !isInCutMode, map, segments });

  /**
   * @param segmentIndex the dragged handle's segment, which must be the one being edited; omitted for a click
   */
  const getAdjacentEndpoint = useCallback(
    (end: SegmentEnd, coordinates: Coordinates, segmentIndex?: number): LatLng | undefined => {
      const { activeSegmentIndex, isEnabled, map, segments } = stateRef.current;
      // A segment not added yet is one past the last
      const editingSegmentIndex = activeSegmentIndex ?? segments.length;
      if (!isEnabled || !map || (segmentIndex !== undefined && segmentIndex !== editingSegmentIndex)) {
        return undefined;
      }

      const endpoint = getAdjacentEndpointToLink(segments, editingSegmentIndex, end);
      if (!endpoint) {
        return undefined;
      }

      const endpointPixel = map.project([endpoint[1], endpoint[0]]);
      const coordinatesPixel = map.project([coordinates.lng, coordinates.lat]);
      return Math.hypot(endpointPixel.x - coordinatesPixel.x, endpointPixel.y - coordinatesPixel.y) <= LINK_DISTANCE_PX ? endpoint : undefined;
    },
    [stateRef]
  );

  const link = useCallback(
    (end: SegmentEnd, endpoint: LatLng) => {
      const linkSegments = end === 'start' ? linkActiveSegmentToPreviousSegment : linkActiveSegmentToNextSegment;
      // The ends already meet, so this only sets the link, with no path or undo state of its own
      dispatch(linkSegments({ points: [endpoint, endpoint], freeDraw: false }));
    },
    [dispatch]
  );

  return { getAdjacentEndpoint, link };
};

export default useAdjacentEndpointLinking;
